import { Clipboard } from '@angular/cdk/clipboard';
import { inject } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store, createSelector } from '@ngrx/store';
import {
    catchError, exhaustMap, filter, map, merge, mergeMap, of, switchMap, take, takeUntil, takeWhile, tap,
} from 'rxjs';

import {
    CancelUnsentBlueSageTransactionGQL,
    CaptureBlueSageHoldGQL,
    ChargeBlueSageCardGQL,
    CreateBlueSageClientTokenGQL,
    CreateTransactionGQL,
    DeleteBlueSagePaymentProfileGQL,
    GetBlueSageGQL,
    GetBlueSagePaymentProfilesGQL,
    GetConfigValuesGQL,
    JobsV3PageGQL,
    ResendBlueSagePayLinkGQL,
    TransactionStage,
} from 'src/generated/graphql.generated';

import {
    selectBlueSageCardRailsAvailable,
    selectMayTakeBlueSageCardPayment,
} from '../../bluesage/bluesage-job.selectors';
import { blueSageTerminalPickerFeature } from '../../bluesage/terminal-picker/bluesage-terminal-picker.feature';
import { BlueSageTerminalPushActions } from '../../bluesage/terminal-push/bluesage-terminal-push.actions';
import { JobToolActions } from '../../job/job-tool.actions';
import { selectJobId } from '../../job/job-tool.reducer';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { selectJobCollectibles } from '../transaction-job.selectors';

import { CreateTransactionDialogActions } from './create-transaction-dialog.actions';
import { createTransactionDialogFeature } from './create-transaction-dialog.feature';
import {
    selectCustomerReachable,
    selectNewInvoiceOffer,
    selectSelectedTargets,
} from './create-transaction-dialog.selectors';
import {
    PaymentTypeConfig,
    CreateTransactionStep,
    CreateTransactionTarget,
} from './create-transaction-dialog.state';
import {
    TERMINAL_CHANNEL,
    filingVariables,
    inferTransactionTarget,
    serializeRemarks,
} from './create-transaction-dialog.util';

const DIALOG_OPENED_ACTIONS = [
    CreateTransactionDialogActions.createTransactionButtonClicked,
    CreateTransactionDialogActions.dispatchCardCreateTransactionButtonClicked,
    CreateTransactionDialogActions.workOrderCreateTransactionButtonClicked,
    CreateTransactionDialogActions.invoicePayButtonClicked,
    CreateTransactionDialogActions.bookingPayButtonClicked,
    CreateTransactionDialogActions.completeAuthorizationFromRowClicked,
] as const;

const DIALOG_PRESELECTING_ACTIONS = [
    CreateTransactionDialogActions.workOrderCreateTransactionButtonClicked,
    CreateTransactionDialogActions.invoicePayButtonClicked,
    CreateTransactionDialogActions.bookingPayButtonClicked,
] as const;

const DIALOG_ASKING_ACTIONS = [
    CreateTransactionDialogActions.createTransactionButtonClicked,
    CreateTransactionDialogActions.dispatchCardCreateTransactionButtonClicked,
] as const;

const JOB_RESOLVE = [ 'discounts', 'users', 'locations', 'distances', 'artifacts', 'charges' ];

const readJob = (
    jobsV3GQL: JobsV3PageGQL,
    jobId: string,
) => jobsV3GQL.fetch({ jobId, resolve: JOB_RESOLVE }, { fetchPolicy: 'network-only' }).pipe(
    map((res) => {
        const [ job ] = res.data.jobs.jobs;

        return job
            ? JobToolActions.fetchJobSuccess({ job, isCached: false })
            : CreateTransactionDialogActions.jobLoadError({
                jobId, error: new Error('Job not found'),
            });
    }),
    catchError((error) =>
        of(CreateTransactionDialogActions.jobLoadError({ jobId, error }))),
);

export const loadDialogJob = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    jobsV3GQL = inject(JobsV3PageGQL),
) => {
    return actions$.pipe(
        ofType(...DIALOG_OPENED_ACTIONS),
        concatLatestFrom(() => store.select(selectJobId)),
        filter(([ { jobId }, routeJobId ]) => jobId !== routeJobId),
        mergeMap(([ { jobId } ]) => readJob(jobsV3GQL, jobId)),
    );
}, { functional: true, dispatch: true });

export const refreshDialogJob = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    jobsV3GQL = inject(JobsV3PageGQL),
) => {
    return actions$.pipe(
        ofType(JobToolActions.backgroundRefreshTriggered),
        concatLatestFrom(() => store.select(selectJobId)),
        filter(([ { jobId }, routeJobId ]) => jobId !== routeJobId),
        mergeMap(([ { jobId } ]) => readJob(jobsV3GQL, jobId)),
    );
}, { functional: true, dispatch: true });

export const loadBlueSagePaymentType = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    getBlueSageGQL = inject(GetBlueSageGQL),
) => {
    return actions$.pipe(
        ofType(...DIALOG_OPENED_ACTIONS),
        switchMap((action) => of(action).pipe(
            concatLatestFrom(() => store.select(selectBlueSageCardRailsAvailable)),
            // Requirement: `queryBlueSage` guards `bluesage.query`, so an operator
            // without it must not be asked here — the read is refused with a
            // config-sounding error on a screen that may only need to record cash.
            filter(([ , cardRailsAvailable ]) => cardRailsAvailable),
            mergeMap(() => getBlueSageGQL.fetch({}, { fetchPolicy: 'no-cache' }).pipe(
                map((res) => CreateTransactionDialogActions.loadPaymentTypeSuccess({
                    // Requirement: `transactions.create` takes `paymentTypeId` as the
                    // Config node's own id, which is what `blueSage.paymentType.configId`
                    // returns.
                    paymentTypeId: res.data.blueSage.paymentType.configId ?? undefined,
                    differentialPct: res.data.blueSage.differentialPct ?? null,
                    differentialPctKnown: res.data.blueSage.differentialPctKnown,
                    holdsEnabled: res.data.blueSage.holdsEnabled === true,
                })),
            )),
            catchError((error) =>
                of(CreateTransactionDialogActions.loadPaymentTypeError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

export const loadPaymentTypes = createEffect((
    actions$ = inject(Actions),
    getConfigValuesGQL = inject(GetConfigValuesGQL),
) => {
    return actions$.pipe(
        ofType(...DIALOG_OPENED_ACTIONS),
        switchMap((action) => of(action).pipe(
            mergeMap(() => getConfigValuesGQL.fetch(
                { keys: [ 'payment-type.*' ] }, { fetchPolicy: 'no-cache' },
            ).pipe(
                map((res) => CreateTransactionDialogActions.loadPaymentTypesSuccess({
                    paymentTypes: readPaymentTypes(res.data.getConfigValues),
                })),
            )),
            catchError((error) =>
                of(CreateTransactionDialogActions.loadPaymentTypesError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

// Requirement: keep every payment type, usable or not. The method grid must
// tell the operator whether the zone has no such type, switched it off, or
// points it at a provider — an absence can't distinguish those. Filtering to
// what's usable happens at the point of use — see `usablePaymentTypes`.
function readPaymentTypes(
    configs: { id?: string; key?: string; value?: string }[] | undefined,
): PaymentTypeConfig[] {
    const options: PaymentTypeConfig[] = [];

    for (const config of configs ?? []) {
        try {
            const parsed = JSON.parse(config?.value ?? '{}');
            if (parsed?.name && config.id && config.key) {
                options.push({
                    id: config.id,
                    key: config.key,
                    name: parsed.name,
                    enabled: Boolean(parsed.enabled),
                    provider: parsed.provider || null,
                });
            }
        } catch {
            // Requirement: a malformed payment-type config must not block this
            // screen from opening — skip it, it's the settings panel's problem.
            continue;
        }
    }

    return options;
}

const onceJobKnown = (store: Store, step: CreateTransactionStep) =>
    store.select(createSelector({
        open: createTransactionDialogFeature.selectOpen,
        step: createTransactionDialogFeature.selectStep,
        collectibles: selectJobCollectibles,
        targets: selectSelectedTargets,
    })).pipe(
        takeWhile((state) => state.open && state.step === step),
        filter((state) => Boolean(state.collectibles)),
        take(1),
    );

// Requirement: an operator opening this dialog on a job that holds money
// means to settle one of those holds rather than collect something new — so
// the holds are the question, and the wizard sits behind an escape hatch.
// Collecting over the top of a hold leaves the customer's funds frozen until
// the issuer expires them.
//
// Requirement: gated on permission to complete, since completing is the only
// action this screen offers on a hold — an operator who can't complete would
// be shown a list of holds they cannot act on.
export const askAboutOpenHolds = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(...DIALOG_ASKING_ACTIONS),
        switchMap(() => onceJobKnown(store, 'targets').pipe(
            concatLatestFrom(() => store.select(selectMayTakeBlueSageCardPayment)),
            filter(([ { collectibles }, mayComplete ]) =>
                mayComplete && (collectibles?.openPreauths.length ?? 0) > 0),
            map(() => CreateTransactionDialogActions.openHoldsFoundOnJob()),
        )),
    );
}, { functional: true, dispatch: true });

export const preselectTarget = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(...DIALOG_ASKING_ACTIONS),
        switchMap(() => onceJobKnown(store, 'targets').pipe(
            concatLatestFrom(() => store.select(selectNewInvoiceOffer)),
            map(([ { collectibles }, newInvoiceOffer ]) =>
                collectibles && inferTransactionTarget(collectibles, Boolean(newInvoiceOffer))),
            filter((target): target is CreateTransactionTarget => Boolean(target)),
            map((target) => CreateTransactionDialogActions.targetInferredFromJob({
                target,
            })),
        )),
    );
}, { functional: true, dispatch: true });

export const preselectNewInvoice = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(...DIALOG_ASKING_ACTIONS),
        switchMap(() => onceJobKnown(store, 'targets').pipe(
            concatLatestFrom(() => store.select(selectNewInvoiceOffer)),
            filter(([ { collectibles }, newInvoiceOffer ]) => Boolean(newInvoiceOffer)
                && collectibles.openInvoices.length === 0
                && collectibles.openPreauths.length === 0),
            map(() => CreateTransactionDialogActions.newInvoicePreselected()),
        )),
    );
}, { functional: true, dispatch: true });

export const preselectDeposit = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(CreateTransactionDialogActions.preselectedTargetUnavailable),
        concatLatestFrom(() => [
            store.select(createTransactionDialogFeature.selectStep),
            store.select(selectJobCollectibles),
            store.select(selectNewInvoiceOffer),
        ]),
        filter(([ , step ]) => step === 'targets'),
        filter(([ , , collectibles ]) => (collectibles?.openInvoices.length ?? 0) === 0),
        map(([ , , , newInvoiceOffer ]) => (newInvoiceOffer
            ? CreateTransactionDialogActions.newInvoicePreselected()
            : CreateTransactionDialogActions.depositPreselected())),
    );
}, { functional: true, dispatch: true });

// Requirement: the amount must track the job's live figures, not a stale
// one — an invoice paid elsewhere while this dialog sits open must not be
// collected again.
export const seedAmountFromTargets = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(
            CreateTransactionDialogActions.targetTileClicked,
            CreateTransactionDialogActions.targetInferredFromJob,
            CreateTransactionDialogActions.newInvoiceTileClicked,
            CreateTransactionDialogActions.continueToAmountButtonClicked,
        ),
        concatLatestFrom(() => store.select(selectSelectedTargets)),
        map(([ , targets ]) => CreateTransactionDialogActions.amountSeededFromTargets({
            amountMinor: targets.totalMinor,
        })),
    );
}, { functional: true, dispatch: true });

export const seedAmountFromPreselectedTarget = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(...DIALOG_PRESELECTING_ACTIONS),
        filter((action) => !action.amountMinor),
        switchMap(() => onceJobKnown(store, 'method').pipe(
            filter(({ targets }) => targets.totalMinor > 0),
            map(({ targets }) => CreateTransactionDialogActions.amountSeededFromTargets({
                amountMinor: targets.totalMinor,
            })),
        )),
    );
}, { functional: true, dispatch: true });

// Requirement: a hold is completed for at most what the job still owes. A
// completion over the balance cannot be applied to any invoice, so the excess
// is stranded on the job until someone refunds it.
export const seedCompleteAmountFromBalance = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(
            CreateTransactionDialogActions.completeAuthorizationFromRowClicked,
            CreateTransactionDialogActions.holdTileClicked,
        ),
        switchMap(({ amountMinor }) => onceJobKnown(store, 'complete').pipe(
            takeUntil(actions$.pipe(ofType(CreateTransactionDialogActions.completeAmountChanged))),
            map(({ collectibles }) => CreateTransactionDialogActions.completeAmountSeededFromBalance({
                amountMinor: Math.min(amountMinor, collectibles?.balanceMinor ?? 0),
            })),
        )),
    );
}, { functional: true, dispatch: true });

// Requirement: an invoice that settled between the menu being drawn and Pay
// being pressed must not be collected against — step one is asked again
// instead of opening a method screen for gone money.
export const dropUnavailablePreselection = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(...DIALOG_PRESELECTING_ACTIONS),
        switchMap(() => onceJobKnown(store, 'method').pipe(
            filter(({ targets }) => targets.totalMinor <= 0),
            map(() => CreateTransactionDialogActions.preselectedTargetUnavailable()),
        )),
    );
}, { functional: true, dispatch: true });

export const refreshJobAfterFailedNewInvoice = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(
            CreateTransactionDialogActions.createTransactionError,
            CreateTransactionDialogActions.chargeCardError,
        ),
        concatLatestFrom(() => store.select(
            createTransactionDialogFeature.selectCreateTransactionDialogState,
        )),
        filter(([ , state ]) => state.open && state.target?.kind === 'newInvoice' && !state.raisedInvoiceId),
        map(([ , state ]) => JobToolActions.backgroundRefreshTriggered({ jobId: state.jobId })),
    );
}, { functional: true, dispatch: true });

export const warnWhenNewInvoiceFallsShort = createEffect((
    actions$ = inject(Actions),
    notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
        ofType(
            CreateTransactionDialogActions.createTransactionSuccess,
            CreateTransactionDialogActions.chargeTransactionCreated,
            CreateTransactionDialogActions.recordPaymentSuccess,
            CreateTransactionDialogActions.payLinkCreated,
            CreateTransactionDialogActions.squareCheckoutLinkSent,
        ),
        filter(({ data }) => Boolean(data.createTransaction.invoiceWarning)),
        tap(({ data }) => notify.addToast.next({
            severity: 'warn',
            summary: 'The invoice was not fully created',
            detail: data.createTransaction.invoiceWarning,
            sticky: true,
        })),
    );
}, { functional: true, dispatch: false });

// Requirement: a double-press must never create two transactions. The push is
// idempotent by derived key, but two rows on a job are two rows nothing
// reconciles.
export const createTransaction = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    createTransactionGQL = inject(CreateTransactionGQL),
) => {
    return actions$.pipe(
        ofType(
            CreateTransactionDialogActions.sendToTerminalButtonClicked,
            CreateTransactionDialogActions.sendPayLinkButtonClicked,
            CreateTransactionDialogActions.recordPaymentButtonClicked,
        ),
        exhaustMap((action) => of(action).pipe(
            // Requirement: the push goes to the terminal the operator was looking at when
            // they pressed, not to whichever one is selected by the time
            // `transactions.create` answers — so all four are read at the click.
            concatLatestFrom(() => [
                store.select(
                    createTransactionDialogFeature.selectCreateTransactionDialogState,
                ),
                store.select(selectSelectedTargets),
                store.select(blueSageTerminalPickerFeature.selectSelectedEpi),
                store.select(selectCustomerReachable),
            ]),
            mergeMap(([ , state, targets, epiAtClick, customerReachable ]) => {
                const payLink =
                    action.type
                    === CreateTransactionDialogActions.sendPayLinkButtonClicked.type;
                const recording =
                    action.type
                    === CreateTransactionDialogActions.recordPaymentButtonClicked.type;

                const authorize = action.type
                    === CreateTransactionDialogActions.sendToTerminalButtonClicked.type
                    && action.intent === 'preauth';

                const paymentTypeId = recording ? state.selectedPaymentTypeId : state.paymentTypeId;

                if (!paymentTypeId) {
                    // Reaching a rail without a payment type produces a transaction nothing can
                    // refund later.
                    throw new Error(recording
                        ? 'No manual payment type is set up in this zone.'
                        : 'BlueSage is not set up as a payment type in this zone yet, so a card '
                            + 'payment cannot be recorded against this job.');
                }

                const remarks = recording ? serializeRemarks(state.remarks) : undefined;

                // Requirement: no receipt where the record names nowhere to send one. The
                // toggle is hidden in that case, so this stops a state left ticked from a
                // reachable customer standing as an answer for an unreachable one.
                const sendReceipt = state.sendReceipt && customerReachable;

                return createTransactionGQL.mutate({
                    jobId: state.jobId,
                    customerId: state.customerId,
                    paymentTypeId,
                    amount: state.amountMinor,
                    ...filingVariables(targets),
                    stage: recording ? TransactionStage.Paid : TransactionStage.Pending,
                    ...(remarks ? { remarks } : {}),
                    // A recorded payment is `paid` on arrival, so the create itself decides its
                    // receipt. A card rail's row is created `pending` and receipt generation returns
                    // early on anything not yet paid, so that answer travels on the metadata below.
                    ...(recording ? { disableSendReceipt: !sendReceipt } : {}),
                    metadata: {
                        // Requirement: the terminal path suppresses the pay link. Its
                        // ABSENCE on the pay-link path is what mints one.
                        ...(recording || payLink ? {} : { bluesageChannel: TERMINAL_CHANNEL }),
                        // Requirement: paying is not consent to store a card, so vaulting is opt-in and
                        // absent unless the operator ticked it.
                        ...(payLink && state.vaultOnPayLink ? { bluesageVaultCard: true } : {}),
                        // Requirement: a hold link only when the operator chose one on the
                        // button. Absent, the link is a sale exactly as before.
                        ...(payLink && state.holdInsteadOfCharge ? { bluesageHold: true } : {}),
                        ...(!recording && sendReceipt ? { bluesageSendReceipt: true } : {}),
                    },
                }).pipe(
                    concatLatestFrom(() =>
                        store.select(createTransactionDialogFeature.selectSession)),
                    map(([ res, session ]) => {
                        const created = res.data.createTransaction.transaction;

                        // Requirement: closing the dialog mid-create cancels the operation — the row is
                        // taken back rather than left on the job as a pending payment nobody asked for,
                        // which the reopened dialog would push against at ITS amount.
                        //
                        // Only the rails whose row exists purely to be pushed against: a recorded
                        // payment is money already in hand and a pay link may already be in the
                        // customer's hands, so neither is ours to withdraw.
                        if (session !== state.session && !recording && !payLink) {
                            return CreateTransactionDialogActions
                                .abandonedTransactionCreated({
                                    transactionId: created.id,
                                    jobId: state.jobId,
                                    data: res.data,
                                });
                        }

                        if (recording) {
                            return CreateTransactionDialogActions.recordPaymentSuccess({
                                data: res.data,
                            });
                        }
                        if (payLink) {
                            return CreateTransactionDialogActions.payLinkCreated({
                                // The row a resend will name to withdraw it.
                                transactionId: created.id,
                                payUrl: created.metadata?.bluesagePayUrl,
                                data: res.data,
                            });
                        }

                        return CreateTransactionDialogActions.createTransactionSuccess({
                            transactionId: created.id,
                            authorize,
                            amountMinor: Math.abs(created.amount ?? state.amountMinor),
                            epi: epiAtClick,
                            data: res.data,
                        });
                    }),
                );
            }),
            catchError((error) =>
                of(CreateTransactionDialogActions.createTransactionError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

// Requirement: Square's payment type is a single named config, resolved the same way
// as cash or check — never BlueSage's singular `state.paymentTypeId` — so this reads
// `selectedPaymentTypeId`, not `paymentTypeId`. The backend takes it from there:
// `generateSquarePaymentLinkOrder` builds the Checkout order and sends the link by SMS
// and email itself, and never returns a URL for this dialog to show or copy.
export const sendSquareCheckout = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    createTransactionGQL = inject(CreateTransactionGQL),
) => {
    return actions$.pipe(
        ofType(CreateTransactionDialogActions.sendSquareCheckoutButtonClicked),
        exhaustMap((action) => of(action).pipe(
            concatLatestFrom(() => [
                store.select(createTransactionDialogFeature.selectCreateTransactionDialogState),
                store.select(selectSelectedTargets),
            ]),
            mergeMap(([ , state, targets ]) => {
                const remarks = serializeRemarks(state.remarks);

                return createTransactionGQL.mutate({
                    jobId: state.jobId,
                    customerId: state.customerId,
                    paymentTypeId: state.selectedPaymentTypeId,
                    amount: state.amountMinor,
                    ...filingVariables(targets),
                    stage: TransactionStage.Pending,
                    ...(remarks ? { remarks } : {}),
                }).pipe(
                    map((res) => CreateTransactionDialogActions.squareCheckoutLinkSent({
                        transactionId: res.data.createTransaction.transaction.id,
                        data: res.data,
                    })),
                );
            }),
            catchError((error) =>
                of(CreateTransactionDialogActions.createTransactionError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

export const pushCreatedTransactionToTerminal = createEffect((
    actions$ = inject(Actions),
) => {
    return actions$.pipe(
        ofType(CreateTransactionDialogActions.createTransactionSuccess),
        // Requirement: the row's amount and the terminal ride on the success, captured
        // at the click — the dialog can be closed and reopened at a different figure, or
        // the picker moved, while the create is in flight. The server refuses a push
        // whose amount disagrees with its row anyway.
        filter(({ epi }) => Boolean(epi)),
        map(({ transactionId, authorize, amountMinor, epi }) =>
            BlueSageTerminalPushActions.pushRequested({
                transactionId,
                epi,
                amountMinor,
                kind: authorize ? 'auth' : 'sale',
            })),
    );
}, { functional: true, dispatch: true });

// Each client token is single-use, so a double-press would mint two — framing
// one and silently wasting the other.
export const openBlueSageCardForm = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    clientTokenGQL = inject(CreateBlueSageClientTokenGQL),
) => {
    return actions$.pipe(
        ofType(CreateTransactionDialogActions.keyInCardButtonClicked),
        exhaustMap((action) => of(action).pipe(
            concatLatestFrom(() =>
                store.select(createTransactionDialogFeature.selectCustomerId)),
            mergeMap(([ , customerId ]) => clientTokenGQL.mutate({ customerId }).pipe(
                map((res) => {
                    const embedUrl = res.data.createBlueSageClientToken.embedUrl;

                    if (!embedUrl) {
                        return CreateTransactionDialogActions.cardFormError({
                            error: new Error(
                                'BlueSage did not return a card form to open. Nothing was charged '
                                + '— try again, or take the card on a terminal.',
                            ),
                        });
                    }

                    return CreateTransactionDialogActions.cardFormOpened({ embedUrl });
                }),
            )),
            catchError((error) =>
                of(CreateTransactionDialogActions.cardFormError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

export const loadBlueSageCardsOnFile = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    profilesGQL = inject(GetBlueSagePaymentProfilesGQL),
) => {
    return actions$.pipe(
        ofType(
            ...DIALOG_OPENED_ACTIONS,
            CreateTransactionDialogActions.chargeCardSuccess,
            CreateTransactionDialogActions.cardHeldSuccess,
        ),
        switchMap((action) => of(action).pipe(
            concatLatestFrom(() => [
                store.select(createTransactionDialogFeature.selectCustomerId),
                store.select(selectBlueSageCardRailsAvailable),
            ]),
            filter(([ , customerId, cardRailsAvailable ]) =>
                Boolean(customerId) && cardRailsAvailable),
            mergeMap(([ , customerId ]) => profilesGQL.fetch(
                { customerId }, { fetchPolicy: 'no-cache' },
            ).pipe(
                map((res) => CreateTransactionDialogActions.cardsOnFileLoaded({
                    cardsOnFile: res.data.blueSagePaymentProfiles,
                })),
            )),
            catchError((error) =>
                of(CreateTransactionDialogActions.cardsOnFileError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

export const deleteBlueSageCardOnFile = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    deleteGQL = inject(DeleteBlueSagePaymentProfileGQL),
) => {
    return actions$.pipe(
        ofType(CreateTransactionDialogActions.deleteCardOnFileClicked),
        exhaustMap((action) => of(action).pipe(
            concatLatestFrom(() =>
                store.select(createTransactionDialogFeature.selectCustomerId)),
            mergeMap(([ , customerId ]) => deleteGQL.mutate({
                customerId,
                token: action.token,
            }).pipe(
                map(() => CreateTransactionDialogActions.deleteCardOnFileSuccess({
                    token: action.token,
                })),
            )),
            catchError((error) =>
                of(CreateTransactionDialogActions.deleteCardOnFileError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

export const chargeBlueSageCard = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    createTransactionGQL = inject(CreateTransactionGQL),
    chargeGQL = inject(ChargeBlueSageCardGQL),
) => {
    return actions$.pipe(
        ofType(CreateTransactionDialogActions.chargeCardButtonClicked),
        exhaustMap((action) => of(action).pipe(
            concatLatestFrom(() => [
                store.select(
                    createTransactionDialogFeature.selectCreateTransactionDialogState,
                ),
                store.select(selectSelectedTargets),
                store.select(selectCustomerReachable),
            ]),
            mergeMap(([ , state, targets, customerReachable ]) => {
                if (!state.paymentTypeId) {
                    throw new Error(
                        'BlueSage is not set up as a payment type in this zone yet, so a card '
                        + 'payment cannot be recorded against this job.',
                    );
                }

                // Checked where the money is sent and not only where it is pressed: this is the
                // difference between charging a chosen card and charging whichever one is lying
                // around.
                if (!state.selectedCard) {
                    throw new Error('Choose a card first — nothing has been charged.');
                }

                return createTransactionGQL.mutate({
                    jobId: state.jobId,
                    customerId: state.customerId,
                    paymentTypeId: state.paymentTypeId,
                    amount: state.amountMinor,
                    ...filingVariables(targets),
                    stage: TransactionStage.Pending,
                    // A keyed-in card is not a terminal payment, but it is equally not something to
                    // mint a pay link for — the customer is right here, and the channel suppresses
                    // the link the same way.
                    metadata: {
                        bluesageChannel: TERMINAL_CHANNEL,
                        // Requirement: the HOLD is decided on the row, at create time. The charge
                        // reads it back from there, so a retry sends what the first attempt sent.
                        ...(state.holdInsteadOfCharge ? { bluesageHold: true } : {}),
                        ...(state.sendReceipt && customerReachable
                            ? { bluesageSendReceipt: true }
                            : {}),
                    },
                }).pipe(
                    // Requirement: the row is recorded BEFORE the charge. A declined card takes the
                    // error path, which never reaches `createTransactionSuccess`, so nothing would
                    // hold the id — leaving a `pending` row for money never taken, and another on
                    // every retry.
                    tap((created) => store.dispatch(
                        CreateTransactionDialogActions.chargeTransactionCreated({
                            transactionId: created.data.createTransaction.transaction.id,
                            data: created.data,
                        }),
                    )),
                    // Requirement: the customer and the amount are NOT sent — the server derives
                    // both from the transaction, because a client-named customer is how a card
                    // belonging to someone else gets charged onto a job the caller controls.
                    mergeMap((created) => chargeGQL.mutate({
                        transactionId: created.data.createTransaction.transaction.id,
                        // Requirement: driven by the selection, not by which fields are populated. The
                        // screen holds a keyed-in card and the saved ones at once, so charging on "is
                        // there a cardToken?" would charge a card the operator had clicked away from.
                        ...(state.selectedCard?.kind === 'onFile'
                            ? { paymentProfile: state.selectedCard.token }
                            : { cardToken: state.cardToken, vault: state.saveCard }),
                    }).pipe(
                        // What BlueSage DID, not what was asked: a hold answered with a sale
                        // was charged, and is reported as a charge.
                        map((charged) => (charged.data?.chargeBlueSageCard?.held
                            ? CreateTransactionDialogActions.cardHeldSuccess({ data: created.data })
                            : CreateTransactionDialogActions.chargeCardSuccess({ data: created.data }))),
                    )),
                );
            }),
            catchError((error) =>
                of(CreateTransactionDialogActions.chargeCardError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

// Requirement: an ONLINE hold — a keyed card or a hold link — has no device, so
// completing it is a capture: one request, answered at once. The amount is the
// base the operator entered; BlueSage adds the card differential at the hold's
// rate, exactly as every other card payment here does.
export const captureOnlineHold = createEffect((
    actions$ = inject(Actions),
    captureGQL = inject(CaptureBlueSageHoldGQL),
) => {
    return actions$.pipe(
        ofType(CreateTransactionDialogActions.captureHoldConfirmed),
        exhaustMap(({ transactionId, amountMinor }) => captureGQL.mutate({
            transactionId,
            amount: amountMinor,
        }).pipe(
            map(() => CreateTransactionDialogActions.captureHoldSuccess({ transactionId })),
            catchError((error) => of(CreateTransactionDialogActions.captureHoldError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

// Requirement: a preauth is completed from the CRM and not on the device. A
// preauth completed ON the terminal produces a standalone-origin transaction and
// permanently loses partial-refund capability; pushed from here it stays
// Connect-origin and keeps partials.
export const leaveCompleteStepAfterConfirm = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(BlueSageTerminalPushActions.completePreauthConfirmed),
        concatLatestFrom(() => store.select(createTransactionDialogFeature.selectStep)),
        filter(([ , step ]) => step === 'complete'),
        map(() => CreateTransactionDialogActions.completeCancelled()),
    );
}, { functional: true, dispatch: true });

// Requirement: a terminal payment is NOT in here. The operator is at the device
// when it settles, so the confirmation has to survive them walking back to the
// computer — the push panel holds it, and its Done closes the dialog below.
//
// A Square Checkout link is closed the same way even though the money hasn't landed
// yet: the backend already sent the link itself and returns nothing for this dialog
// to show or copy, so there is nothing left on screen worth keeping the dialog open
// for.
export const closeDialogWhenMoneyLands = createEffect((
    actions$ = inject(Actions),
) => {
    return actions$.pipe(
        ofType(
            CreateTransactionDialogActions.recordPaymentSuccess,
            CreateTransactionDialogActions.chargeCardSuccess,
            CreateTransactionDialogActions.cardHeldSuccess,
            CreateTransactionDialogActions.captureHoldSuccess,
            CreateTransactionDialogActions.squareCheckoutLinkSent,
        ),
        map(() => CreateTransactionDialogActions.dialogClosed()),
    );
}, { functional: true, dispatch: true });

// Requirement: only where the device took or held the money. On a decline or a
// timeout nothing was taken and Done is the documented way back to the form for
// a fresh send, so those must leave the dialog standing.
export const closeDialogWhenPushPanelDone = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(BlueSageTerminalPushActions.doneButtonClicked),
        concatLatestFrom(() => store.select(createTransactionDialogFeature.selectOpen)),
        filter(([ { phase }, open ]) => open && (phase === 'settled' || phase === 'authorized')),
        map(() => CreateTransactionDialogActions.dialogClosed()),
    );
}, { functional: true, dispatch: true });

export const showBlueSagePushWhileCreating = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(CreateTransactionDialogActions.sendToTerminalButtonClicked),
        concatLatestFrom(() => [
            store.select(blueSageTerminalPickerFeature.selectSelectedEpi),
            store.select(createTransactionDialogFeature.selectAmountMinor),
        ]),
        filter(([ , epi ]) => Boolean(epi)),
        map(([ { intent }, epi, amountMinor ]) => BlueSageTerminalPushActions.pushPreparing({
            epi,
            amountMinor,
            kind: intent === 'preauth' ? 'auth' : 'sale',
        })),
    );
}, { functional: true, dispatch: true });

export const resetBlueSagePushWithDialog = createEffect((
    actions$ = inject(Actions),
) => {
    return actions$.pipe(
        ofType(
            ...DIALOG_OPENED_ACTIONS,
            CreateTransactionDialogActions.dialogClosed,
        ),
        map(() => BlueSageTerminalPushActions.panelDismissed()),
    );
}, { functional: true, dispatch: true });

export const clearBlueSagePushWhenCreateFails = createEffect((
    actions$ = inject(Actions),
) => {
    return actions$.pipe(
        ofType(CreateTransactionDialogActions.createTransactionError),
        map(() => BlueSageTerminalPushActions.panelDismissed()),
    );
}, { functional: true, dispatch: true });


export const cancelUnsentTransaction = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    cancelUnsentGQL = inject(CancelUnsentBlueSageTransactionGQL),
) => {
    const failed$ = actions$.pipe(
        ofType(
            CreateTransactionDialogActions.chargeCardError,
            BlueSageTerminalPushActions.pushError,
            BlueSageTerminalPushActions.pushRefusedAfterStart,
        ),
        concatLatestFrom(() => store.select(createTransactionDialogFeature.selectCreatedTransactionId)),
        map(([ , transactionId ]) => transactionId),
    );
    const abandoned$ = actions$.pipe(
        ofType(CreateTransactionDialogActions.abandonedTransactionCreated),
        map(({ transactionId }) => transactionId),
    );

    return merge(failed$, abandoned$).pipe(
        filter(Boolean),
        mergeMap((transactionId) => cancelUnsentGQL.mutate({ transactionId }).pipe(
            map((res) => res.data.cancelUnsentBlueSageTransaction),
            filter((transaction) => transaction.stage === TransactionStage.Cancelled),
            map((transaction) => CreateTransactionDialogActions.unsentTransactionCancelled({ transaction })),
            catchError((error) =>
                of(CreateTransactionDialogActions.unsentTransactionCancelError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

export const withdrawRetryForCancelledTransaction = createEffect((
    actions$ = inject(Actions),
) => {
    return actions$.pipe(
        ofType(CreateTransactionDialogActions.unsentTransactionCancelled),
        map(({ transaction }) =>
            BlueSageTerminalPushActions.failedTransactionDiscarded({ transactionId: transaction.id })),
    );
}, { functional: true, dispatch: true });

// The URL comes from the action rather than state, so the button copies the link
// the operator is looking at even if a second is minted while they reach for it.
export const copyBlueSagePayLink = createEffect((
    actions$ = inject(Actions),
    clipboard = inject(Clipboard),
) => {
    return actions$.pipe(
        ofType(CreateTransactionDialogActions.payLinkCopyButtonClicked),
        tap(({ payUrl }) => clipboard.copy(payUrl)),
    );
}, { functional: true, dispatch: false });

// Requirement: "send a new link" REPLACES the link that is out there, so a job
// never carries two payable URLs for the same money.
export const resendBlueSagePayLink = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    resendGQL = inject(ResendBlueSagePayLinkGQL),
) => {
    return actions$.pipe(
        ofType(CreateTransactionDialogActions.sendNewPayLinkButtonClicked),
        exhaustMap((action) => of(action).pipe(
            concatLatestFrom(() => store.select(
                createTransactionDialogFeature.selectPayLinkTransactionId,
            )),
            mergeMap(([ , transactionId ]) => {
                if (!transactionId) {
                    throw new Error('There is no pay link to replace.');
                }

                return resendGQL.mutate({ transactionId }).pipe(
                    map((res) => {
                        const resent = res.data.resendBlueSagePayLink;
                        const created = resent.created.transaction;

                        return CreateTransactionDialogActions.payLinkResent({
                            transactionId: created.id,
                            payUrl: created.metadata?.bluesagePayUrl,
                            created,
                            cancelled: resent.cancelled,
                        });
                    }),
                );
            }),
            catchError((error) =>
                of(CreateTransactionDialogActions.createTransactionError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });
